import { BaseSelectorOption } from '@/types';
import styled from '@emotion/styled';
import { Autocomplete, Box, Fade, SelectChangeEvent, Stack, Typography } from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { EditTemplate, Switch } from 'klayowebshared';
import { debounce, get, isEmpty } from 'lodash';
import React, { SyntheticEvent } from 'react';
import { AppContext } from '../../common/AppContext';
import axiosClient from '../../common/AxiosClient';
import { ErrorModel } from '../../common/models/ErrorModel';
import { Alert } from '../../components/Alert';
import { Paper } from '../../components/Paper';
import { PaperOrDialog } from '../../components/PaperOrDialog';
import { SelectMenu } from '../../components/SelectMenu';
import { TextField } from '../../components/TextField';
import { Tooltip } from '../../components/Tooltip';
import { TooltipIcon } from '../../components/TooltipIcon';
import { ViewComponent } from '../../components/ViewComponent';
import { ExpiryNotificationSelector } from '../../components/selectors/ExpiryNotificationSelector';
import { MultiAttributeSelector } from '../../components/selectors/MultiAttributeSelector';
import { MultiCategorySelector } from '../../components/selectors/MultiCategorySelector';
import { MultiCourseSelector } from '../../components/selectors/MultiCourseSelector';
import {
  COURSE_RECURRENCE,
  PAGE_SIZE_FOR_ADD_EDIT,
  TableSearchDebounceTime,
} from '../../constants';
import { Data } from '../../data/Data';
import { SettingsCourse, SettingsCourseData } from '../../data/settings/SettingCourse';
import { SettingsCourseList } from '../../data/settings/SettingCourseList';
import { SettingsCatalogueCourseList } from '../../data/settings/SettingsCatalogueCourseList';
import { SettingsAttribute, SettingsAttributeData } from '../../data/settings/SettingsAttribute';
import { SettingsAttributeCategory } from '../../data/settings/SettingsAttributeCategory';
import { SettingsAttributeCategoryList } from '../../data/settings/SettingsAttributeCategoryList';
import { SettingsAttributeList } from '../../data/settings/SettingsAttributeList';
import { withLocale } from '../../providers/LocaleProvider';
import WarningIcon from '../../resources/images/icons-warning.svg';
import { KLAYO_COLORS } from '../../themes';
import type {
  AddEditAttributeViewProps,
  AddEditAttributeViewState,
  ExpiryNotification,
  SuggestionCompetency,
} from '../../types/views/attribute.types';

class AddEditAttributeViewBase extends ViewComponent<
  AddEditAttributeViewProps,
  AddEditAttributeViewState
> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static subPages = {
    standard: { name: 'Standard', slug: '', tooltip: 'A normal competency' },
    parent: {
      name: 'Parent',
      slug: 'parent',
      tooltip: 'A parent competency which depends on its children to become approved',
    },
  };

  existingAttribute: SettingsAttribute;
  debounceTimer: ReturnType<typeof setTimeout> | null;

  constructor(props: AddEditAttributeViewProps) {
    super(props);
    const newAttribute = props.editMode
      ? new SettingsAttribute(props.attribute as any)
      : new SettingsAttribute({
          ...props.attribute,
          name: props.attribute?.name || '',
        } as any);
    this.onSearchCategories = debounce(this.onSearchCategories.bind(this), 300);
    this.onSearchSuggestion = debounce(this.onSearchSuggestion.bind(this), 600);
    this.state = {
      ...this.state,
      attribute: newAttribute,
      categories: null,
      attributes: null,
      trainingCourseSelected: [],
      saving: false,
      error: props.error ?? null,
      expiryEnabled: false,
      expiryEnabledAutoOn: false,
      trainingEnable: false,
      proficiencyEnabled: false,
      hasEdit: false,
      hasSaveAndEdit: false,
      valid: props.attribute ? this.isValid(props.attribute) : false,
      integrationId: null,
      courses: null,
      isSettingRecurrence: false,
      coursesSearch: null,
      categoriesSearch: null,
      attributesSearch: null,
      organizationDialogMode: null,
      isCategoryLoading: false,
      isShowWarningTrainingNotMatch: false,
      suggestionsCompetencies: [],
      isSaving: false,
    };

    this.existingAttribute = new SettingsAttribute(props.attribute as any);
    this.debounceTimer = null;
  }

  addIsValidToCourses(
    courses: SettingsCourse[],
    attribute: SettingsAttribute | null
  ): SettingsCourse[] {
    return (courses || []).map((course: SettingsCourse) => ({
      ...course,
      isValid: !attribute?.hasExpiryDate
        ? true
        : !!(attribute?.hasExpiryDate && course.recurrence !== COURSE_RECURRENCE.NONE.value),
    }));
  }

  componentDidMount() {
    this.loadDetails();
    this.loadChildAttributes();
    this.loadCourses('');
    this.loadCategories();
    this.loadOrganization();

    if (!this.props.editMode) {
      this.loadingSuggestion();
    }

    this.context.eventBus?.on('savingAttribute', (isLoading: boolean) => {
      this.setState({
        isSaving: isLoading,
      });
    });
  }

  componentDidUpdate(
    prevProps: AddEditAttributeViewProps,
    prevState: AddEditAttributeViewState
  ): void {
    const { attribute, hasSaveAndEdit } = this.state;

    // reset form when save & create an other
    if (hasSaveAndEdit && this.props.attribute !== prevProps.attribute) {
      this.setState({
        attribute: this.props.attribute
          ? new SettingsAttribute(this.props.attribute as any)
          : new SettingsAttribute(),
        expiryEnabled: false,
        trainingCourseSelected: [],
        trainingEnable: false,
        hasSaveAndEdit: false,
      });
      this.onSearchSuggestion('');
    }

    // assure that trainingCourseSelected has data
    if (this.state.trainingCourseSelected !== prevState.trainingCourseSelected) {
      this.onCheckDateExpiredAutoOn();
    }

    // check if has selected training course enable training switch
    if (this.state.trainingCourseSelected !== prevState.trainingCourseSelected)
      this.onCheckHasTrainingCourse();

    if (this.props.attribute !== prevProps.attribute) {
      // this.setState({
      //     attribute: this.props.attribute,
      //     valid: this.isValid(this.props.attribute),
      //     expiryEnabled: false,
      //     proficiencyEnabled: false,
      //     saving: false, error: this.props.error
      // });
    }
    if (this.props.error !== prevProps.error) this.setState({ error: this.props.error ?? null });
    if (this.props.location !== prevProps.location)
      this.setState({ valid: this.isValid(attribute) });
    if (
      this.state.trainingEnable !== prevState.trainingEnable ||
      this.state.trainingCourseSelected !== prevState.trainingCourseSelected
    )
      this.setState({ valid: this.isValid(attribute) });
    if (this.props.orgSettings !== prevProps.orgSettings) this.forceUpdate();
  }

  loadDetails(): void {
    const { attribute } = this.state;
    const attributeId = attribute ? attribute.attributeId : this.props.attributeId;
    let trainingCourseSelected: SettingsCourse[] = [];

    if (!attributeId) {
      this.setState({ trainingCourseSelected: [] });
      return;
    }

    this.context.setLoading?.('loading', true);

    Data.get<{ attributeDefinition: SettingsAttributeData }>(
      this.context,
      '/AttributeDefinition/' + attributeId
    )

      .then((response) => {
        const newAttribute = new SettingsAttribute(response.data.attributeDefinition);
        this.existingAttribute = new SettingsAttribute(response.data.attributeDefinition);

        setTimeout(() => {
          // The MERGED list, not response.data.attributeDefinition.trainingCourses: that key holds
          // integration courses only, so catalogue links would render no chip — and because each id
          // field is the complete desired set for its side, the next save would delete them.
          trainingCourseSelected = newAttribute.trainingCourses;

          this.setState({
            attribute: newAttribute,
            trainingCourseSelected,
            valid: this.isValid(attribute),
          });
        }, 0);
      })
      .catch((e: unknown) => {
        this.setState({ error: ErrorModel.parseServerError(e) });
      })
      .finally(() => {
        this.context.setLoading?.('loading', false);
      });
  }

  loadTrainingByCompetenciesId(competencyId: string): void {
    this.context.setLoading?.('training', true);
    const { attribute } = this.state;
    Data.get<{ trainings: SettingsCourseData[] }>(
      this.context,
      `/StandardLibrary/competencies/${competencyId}/linkedtrainings`
    )
      .then((response) => {
        const trainings = get(response, 'data.trainings', []);
        attribute.trainingCourses = trainings.map((course) => {
          return new SettingsCourse(course);
        });
        this.setState({
          attribute,
          trainingEnable: trainings?.length > 0,
          trainingCourseSelected: attribute.trainingCourses,
        });
      })
      .finally(() => {
        this.context.setLoading?.('training', false);
      });
  }

  loadingSuggestion(searchText = ''): void {
    if (!this.context.organization?.enableStandardsLibrary) return;

    this.setState({
      isLoadingSuggestion: true,
    });
    Data.call('post', `/StandardLibrary/competencies`, {
      data: [],
      params: {
        pageNumber: 1,
        pageSize: 5,
        searchText: (searchText || '').trim(),
        excludeExisting: true,
        showExtra: false,
      },
    })
      .then((response: { data: { competencies?: { data?: SuggestionCompetency[] } } }) => {
        const suggestions = get(response, 'data.competencies.data', []) as SuggestionCompetency[];
        this.setState({
          suggestionsCompetencies: suggestions,
        });
      })
      .finally(() => {
        this.setState({
          isLoadingSuggestion: false,
        });
      });
  }

  onSearchSuggestion(searchText: string) {
    this.loadingSuggestion(searchText);
  }

  loadOrganization() {
    this.setState({
      organizationDialogMode: this.context?.organization as any,
    });
  }

  loadCourses(searchText: string): void {
    this.context.setLoading?.('training', true);

    // get setting courses data then set to courses state
    const pagingOptions = {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: searchText,
    };

    // Both providers, in one list. Catalogue (OLC) courses come from their own endpoint because
    // /Integration/Courses cannot return them: it resolves an OrganizationIntegration and guards on
    // it, and filters in memory over IntegrationCourse entities, which catalogue courses are not.
    Promise.all([
      (SettingsCourseList.getWithPaging(this.context, pagingOptions) as any).catch(() => null),
      SettingsCatalogueCourseList.load(this.context, {
        searchText,
        attributeDefinitionId: this.props.attribute?.attributeId ?? this.props.attributeId,
      }).catch(() => null),
    ])
      .then(([integrationCourses, catalogueCourses]) => {
        // Kept as a plain array rather than by mutating the SettingsCourseList: that object is a
        // cached singleton, and constructing an empty one as a fallback throws (its constructor
        // needs a context).
        this.setState({
          courses: [
            ...((integrationCourses?.courses as SettingsCourse[]) ?? []),
            ...(catalogueCourses?.courses ?? []),
          ],
          coursesSearch: searchText,
        });
      })
      .catch(() => {})
      .finally(() => this.context.setLoading?.('training', false));
  }

  /**
   * Sets an OLC course's recurrence from here, so an expiring competency can be linked without a trip
   * to the Training Manager screen — which sits behind a different permission the competency admin
   * may not hold, and which is where this used to dead-end.
   *
   * The recurrence is per-organisation and shared by every competency linked to that course, so the
   * server refuses a change that would contradict another one; that rejection surfaces in the normal
   * error alert.
   */
  onSetCatalogueRecurrence(courseId: string, recurrence: number): void {
    const { attribute } = this.state;
    this.setState({ isSettingRecurrence: true });

    axiosClient
      .put('/AttributeDefinition/CatalogueCourseRecurrence', {
        courseId,
        recurrence,
        // The expiry setting as it stands HERE, which may not be saved yet. Without it the server judges
        // this competency by its stored row and refuses the change that would make the row valid —
        // deadlocking the one case this control exists for.
        attributeDefinitionId: attribute.attributeId ?? null,
        hasExpiryDate: attribute.hasExpiryDate,
      })
      .then(() => {
        // trainingCourseSelected and attribute.trainingCourses hold the SAME SettingsCourse instances,
        // so mutating in place updates both; only the picker's own list is separate. The new array
        // identity is what makes componentDidUpdate re-run onCheckDateExpiredAutoOn, which clears the
        // warning and re-enables Save.
        const { courses, trainingCourseSelected } = this.state;
        const applyRecurrence = (course: SettingsCourse) =>
          course.id === courseId ? Object.assign(course, { recurrence }) : course;

        this.setState({
          courses: courses ? courses.map(applyRecurrence) : courses,
          trainingCourseSelected: trainingCourseSelected.map(applyRecurrence),
          error: null,
        });
      })
      .catch((e: unknown) => this.setState({ error: ErrorModel.parseServerError(e) }))
      .finally(() => this.setState({ isSettingRecurrence: false }));
  }

  onLoadCourseBlur() {
    const { coursesSearch } = this.state;

    if (coursesSearch) {
      this.loadCourses('');
    }
  }

  loadCategories(searchText?: string): void {
    this.context.setLoading?.('categories', true);

    this.setState({
      isCategoryLoading: true,
    });

    const pagingOptions = {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: searchText,
    };

    (SettingsAttributeCategoryList.getWithPaging(this.context, pagingOptions) as any)
      .then((categories: SettingsAttributeCategoryList) => {
        this.setState({ categories, categoriesSearch: searchText ?? null });
      })
      .catch(() => {})
      .finally(() => {
        this.context.setLoading?.('categories', false);

        this.setState({
          isCategoryLoading: false,
        });
      });
  }

  onSearchCourse(searchText: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.loadCourses(searchText);
    }, TableSearchDebounceTime);
  }

  onSearchCategories(searchText: string): void {
    this.loadCategories(searchText);
  }

  onCategoriesBlur() {
    const { categoriesSearch } = this.state;

    if (categoriesSearch) this.loadCategories();
  }

  loadChildAttributes(searchText?: string): void {
    this.context.setLoading?.('attributes', true);

    const pagingOptions = {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: searchText,
    };

    // get setting attributes data then set to attributes state
    (SettingsAttributeList.getWithPaging(this.context, pagingOptions) as any)
      .then((attributes: SettingsAttributeList) => {
        this.setState({ attributes, attributesSearch: searchText ?? null });
      })
      .catch(() => {})
      .finally(() => this.context.setLoading?.('attributes', false));
  }

  onSearchChildAttributes(searchText: string): void {
    this.loadChildAttributes(searchText);
  }

  onChildAttributeBlur() {
    const { attributesSearch } = this.state;

    if (attributesSearch) this.loadChildAttributes();
  }

  onCheckDateExpiredAutoOn(): void {
    const { trainingCourseSelected, attribute } = this.state;

    // Early return if no training courses selected
    if (isEmpty(trainingCourseSelected)) {
      this.setState({
        expiryEnabledAutoOn: false,
        isShowWarningTrainingNotMatch: false,
      });
      return;
    }

    const hasExpiryDate = attribute.hasExpiryDate;
    const allCoursesRecurrence = trainingCourseSelected.every(
      (course: SettingsCourse) => course.recurrence !== COURSE_RECURRENCE.NONE.value
    );
    const allCoursesNoneRecurrence = trainingCourseSelected.every(
      (course: SettingsCourse) => course.recurrence === COURSE_RECURRENCE.NONE.value
    );

    // If attribute has expiry date, check if all courses are recurrent
    if (hasExpiryDate) {
      this.setState({
        expiryEnabledAutoOn: allCoursesRecurrence,
        isShowWarningTrainingNotMatch: !allCoursesRecurrence,
      });
      return;
    }

    // If attribute doesn't have expiry date, check if any course is recurrent
    if (!allCoursesNoneRecurrence) {
      const toggleOnData = { target: { checked: true } };
      this.onExpiryEnabled(toggleOnData);
    } else {
      this.setState({
        expiryEnabledAutoOn: false,
        isShowWarningTrainingNotMatch: false,
      });
    }
  }

  onCheckHasTrainingCourse() {
    const { trainingCourseSelected, organizationDialogMode } = this.state;
    const { organization } = this.props;
    const organizationData = organization ? organization : organizationDialogMode;

    // Deliberately NOT keyed on hasIntegration any more: a competency whose only links are OLC
    // catalogue courses would otherwise open with Training switched off, and toggling it then clears
    // the selection — losing links the admin never saw.
    if (trainingCourseSelected && trainingCourseSelected.length > 0 && organizationData) {
      this.setState({ trainingEnable: true });

      return;
    }
  }

  onTrainingEnable(e: React.ChangeEvent<HTMLInputElement>): void {
    const { attribute } = this.state;

    // check if switch off clear trainingCourses
    if (e.target?.checked === false) {
      attribute.trainingCourses = [];
      this.setState({ trainingCourseSelected: [] });
    }

    this.setState({ trainingEnable: e.target?.checked });
  }

  onCourseAttributeChange(_e: SyntheticEvent | null, courseAttributes: SettingsCourse[]): void {
    const { attribute } = this.state;

    attribute.trainingCourses = courseAttributes;

    this.setState({
      attribute,
      trainingCourseSelected: courseAttributes,
      valid: this.isValid(attribute),
    });
  }

  isValid(attribute: SettingsAttribute | null): boolean {
    // const { trainingEnable } = this.state;
    const trainingEnable = this.state?.trainingEnable;

    if (trainingEnable) {
      return (
        this.isNameValid(attribute) &&
        (!this.isParent(attribute) ||
          (this.isParent(attribute) && this.isChildAttributesValid(attribute))) &&
        this.isTrainingCourseValid(attribute?.trainingCourses)
      );
    }

    return (
      this.isNameValid(attribute) &&
      (!this.isParent(attribute) ||
        (this.isParent(attribute) && this.isChildAttributesValid(attribute)))
    );
  }

  isNameValid(attribute: SettingsAttribute | null): boolean {
    return attribute != null && attribute.name != null && attribute.name.length > 2;
  }

  isChildAttributesValid(attribute: SettingsAttribute | null): boolean {
    return (attribute && attribute.childAttributes.length > 0) === true;
  }

  isTrainingCourseValid(selectedCourse: SettingsCourse[] | undefined): boolean {
    return selectedCourse != null && selectedCourse.length > 0;
  }

  onAttributeTypeChange(e: React.MouseEvent<HTMLElement>): void {
    const { trainingEnable } = this.state;

    // toggle off training when create parent attribute
    if (
      (e.target as HTMLElement).textContent ===
        (AddEditAttributeViewBase as any).subPages.parent.name &&
      trainingEnable
    ) {
      this.setState({
        trainingEnable: false,
        trainingCourseSelected: [],
      });
    }
  }

  onNameChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { attribute } = this.state;
    attribute.name = e.target.value;
    this.setState({ attribute, valid: this.isValid(attribute) });
  }

  onImportCodeChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { attribute } = this.state;
    attribute.importCode = e.target.value;

    this.setState({ attribute });
  }

  onDescriptionChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { attribute } = this.state;
    attribute.description = e.target.value;

    this.setState({ attribute });
  }

  onCategoriesChange(_e: SyntheticEvent | null, categories: SettingsAttributeCategory[]): void {
    const { attribute } = this.state;
    attribute.categories = categories;
    this.setState({ attribute });
  }

  onChildAttributesChange(_e: SyntheticEvent | null, childAttributes: SettingsAttribute[]): void {
    const { attribute } = this.state;
    attribute.childAttributes = childAttributes;

    this.setState({ attribute, valid: this.isValid(attribute) });
  }

  onSave(returnToList?: boolean): void {
    const { onSave, onBlockNavigation } = this.props;
    const { attribute } = this.state;
    let hasSaveAndEdit = false;

    if (!returnToList) hasSaveAndEdit = true;

    // parent attribute not allowed trainingCourse
    if (this.isParent(attribute)) {
      attribute.trainingCourses = [];
      this.setState({ trainingCourseSelected: [] });
    }

    if (onBlockNavigation) onBlockNavigation(false, 'Attribute');
    this.setState({ saving: true, hasSaveAndEdit });

    if (onSave) {
      try {
        const savePromise = onSave(attribute, returnToList);

        if (savePromise && typeof (savePromise as Promise<void>).catch === 'function') {
          (savePromise as Promise<void>).catch(
            (error: { response?: { status: number; data?: { message?: string } } }) => {
              if (error.response && error.response.status === 400) {
                const errorMessage =
                  error.response.data?.message || 'Bad Request: Please check your input data';
                this.setState({
                  error: ErrorModel.parseServerError(error) || errorMessage,
                  saving: false,
                });
              } else {
                this.setState({
                  error: ErrorModel.parseServerError(error),
                  saving: false,
                });
              }
            }
          );
        }
      } catch (error) {
        this.setState({
          error: ErrorModel.parseServerError(error),
          saving: false,
        });
      }
    }
  }

  onSaveAndCreate() {
    this.onSave(false);
  }

  onExpiryEnabled(e: { target: { checked: boolean } }) {
    const { onOpenDialog } = this.props;
    const { attribute, trainingCourseSelected } = this.state;

    // Early return if no training courses selected
    if (!trainingCourseSelected?.length) {
      attribute.hasExpiryDate = e.target.checked;
      this.setState({ attribute });
      return;
    }

    // Check if any course has recurrence
    const hasRecurringCourse = trainingCourseSelected.some(
      (course: SettingsCourse) => course.recurrence !== COURSE_RECURRENCE.NONE.value
    );

    // Prevent disabling expiry when there are recurring courses
    if (hasRecurringCourse && !e.target.checked) {
      onOpenDialog?.();
      return;
    }

    attribute.hasExpiryDate = e.target.checked;
    this.setState({ attribute }, () => {
      this.onCheckDateExpiredAutoOn();
    });
  }

  onProficiencyEnabled(e: React.ChangeEvent<HTMLInputElement>): void {
    const { attribute } = this.state;
    attribute.hasProficiency = e.target.checked;

    this.setState({ attribute });
  }

  onExpiryNotificationChange(expiryNotifications: BaseSelectorOption[]): void {
    const { attribute } = this.state;
    attribute.expiryNotifications = (expiryNotifications ?? []) as any;

    this.setState({ attribute });
  }

  onNavigation(e: React.MouseEvent, callback: (e: React.MouseEvent) => void): void {
    const { onNavigation } = this.props;
    onNavigation && onNavigation(e, callback);
  }

  onCancel(e: React.MouseEvent, _source?: string): void {
    this.onNavigation(e, this.stopBlockingNavAndClose.bind(this));
  }

  stopBlockingNavAndClose(e: React.MouseEvent): void {
    const { onCancel, onBlockNavigation } = this.props;
    if (onBlockNavigation) onBlockNavigation(false, 'Attribute');
    if (onCancel) onCancel(e);
  }

  isParent(attr?: SettingsAttribute | null): boolean {
    const { dialogMode, location } = this.props;
    const attribute = attr ? attr : this.state.attribute;
    const path = location ? location.pathname : '/';
    return (path.includes('parent') && !dialogMode) || (attribute && attribute.isParent) || false;
  }

  onEditStateChange(hasEdit: boolean): void {
    this.setState({ hasEdit });
  }

  onChangeOnSuggestion(_e: SyntheticEvent, newValue: string | SuggestionCompetency | null): void {
    const { attribute } = this.state;
    const isFromSuggestion = typeof newValue === 'object' && newValue !== null;
    this.setState({
      taskName: isFromSuggestion ? newValue.name : (newValue as string) || '',
      isTaskFromSuggestion: isFromSuggestion,
    });
    if (isFromSuggestion) {
      attribute.name = newValue.name;
      this.setState(
        {
          attribute,
          trainingEnable: false,
          valid: this.isValid(attribute),
        },
        () => {
          this.onImportCompetency(newValue);
        }
      );
    }
  }

  onImportCompetency(newValue: SuggestionCompetency): void {
    const { attribute } = this.state;
    attribute.description = newValue.description;
    attribute.hasExpiryDate = newValue?.hasExpiryDate ?? false;
    attribute.categories = newValue.competencyCategories ?? [];
    this.setState(
      {
        attribute,
        valid: this.isValid(attribute),
      },
      () => {
        this.onImportUnknowCategory(newValue);
      }
    );
  }

  onImportUnknowCategory(newValue: SuggestionCompetency): void {
    const { attribute } = this.state;
    this.setState({ isLoadingImportUnknowCategory: true });
    Data.call('post', '/AttributeDefinition/AttributeCategories', {
      data: {
        competencyCategories: newValue?.competencyCategories?.map(
          (category: SettingsAttributeCategory) => {
            return category.name;
          }
        ),
      },
    })
      .then((response: { data: { competencyCategories?: unknown[] } }) => {
        const categories = get(response, 'data.competencyCategories', []) as unknown[];
        attribute.categories = categories.map((category: unknown) => {
          return new SettingsAttributeCategory(category as SettingsAttributeCategory);
        });

        this.setState({ attribute }, () => {
          this.loadTrainingByCompetenciesId(newValue.id);
        });
      })
      .catch(() => {})
      .finally(() => {
        this.setState({ isLoadingImportUnknowCategory: false });
      });
  }

  onChangeNameWithDebounce(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    this.onNameChange(e);
    this.onSearchSuggestion(e.target.value);
  }

  render() {
    const {
      organization,
      orgSettings,
      theme,
      className,
      user,
      dialogMode,
      editMode,
      onShowNewAttributeDialog,
      onShowNewCategoryDialog,
      location,
      onAllowNavigation,
      onBlockNavigation,
    } = this.props;
    const {
      error,
      attribute,
      attributes,
      organizationDialogMode,
      categories,
      valid,
      hasEdit,
      saving,
      courses,
      trainingCourseSelected,
      expiryEnabledAutoOn,
      isShowWarningTrainingNotMatch,
      isSettingRecurrence,
      suggestionsCompetencies,
      isLoadingSuggestion,
      isLoadingImportUnknowCategory,
      isSaving,
    } = this.state;
    // eslint-disable-next-line prefer-const
    let { expiryEnabled, proficiencyEnabled, trainingEnable } = this.state;

    const basePath = '/settings/competencies/new';

    let path = location ? location.pathname : '/';
    if (path === '/') path = basePath;

    const isParent = this.isParent();

    expiryEnabled = expiryEnabled === true || (attribute && attribute.hasExpiryDate === true);
    proficiencyEnabled =
      proficiencyEnabled === true || (attribute && attribute.hasProficiency === true);

    interface ActionItem {
      label: string;
      onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
      primary?: boolean;
      disabled?: boolean;
      variant?: string;
      floatRight?: boolean;
    }

    const translate = (this.props as any).translate;

    const actions: ActionItem[] = [
      {
        label: editMode ? translate('common.save') : translate('common.create'),
        onClick: this.onSave.bind(this) as any,
        primary: true,
        disabled:
          !valid ||
          (editMode && !hasEdit) ||
          isShowWarningTrainingNotMatch ||
          isLoadingImportUnknowCategory ||
          isSaving,
      },
    ];

    const organizationData = organization ? organization : organizationDialogMode;

    if (!editMode && !dialogMode) {
      actions.push({
        label: translate('addEditAttribute.saveAndCreateAnother'),
        variant: 'filled',
        floatRight: theme?.isBreakpointUp('md'),
        disabled:
          !valid || isShowWarningTrainingNotMatch || isLoadingImportUnknowCategory || isSaving,
        onClick: ((e?: React.MouseEvent<HTMLButtonElement>) => {
          this.onSaveAndCreate();
          e?.currentTarget.blur();
        }) as any,
      });
    }

    actions.push({
      label: translate('common.cancel'),
      onClick: this.onCancel.bind(this) as any,
    });

    return (
      <EditTemplate
        theme={theme}
        name='Competencies'
        onAllowNavigation={onAllowNavigation}
        onBlockNavigation={onBlockNavigation}
        onEditStateChange={this.onEditStateChange.bind(this)}
        detectEdit={!saving}
        unsavedChangesTitle={translate('common.unsavedChangesTitle')}
        unsavedChangesQuestion={translate('common.unsavedChangesQuestion')}
        unsavedChangesAcceptButton={translate('common.leavePage')}
        unsavedChangesCancelButton={translate('common.cancel')}
        compare={{
          existing: this.existingAttribute,
          editing: attribute,
          members: [
            { name: 'name' },
            { name: 'description' },
            {
              name: 'categories',
              detectEdit: (existing: SettingsAttribute, editing: SettingsAttribute) =>
                !existing.compareCategories(editing),
            },
            {
              name: 'childAttributes',
              arrayMatch: (i1: SettingsAttribute, i2: SettingsAttribute) =>
                i1.attributeId === i2.attributeId,
            },
            { name: 'hasExpiryDate' },
            { name: 'hasProficiency' },
            {
              name: 'trainingCourses',
              arrayMatch: (i1: SettingsCourse, i2: SettingsCourse) => i1.id === i2.id,
            },
            {
              name: 'expiryNotifications',
              detectEdit: (existing: SettingsAttribute, editing: SettingsAttribute) => {
                const existingNotificationIndex = existing.expiryNotifications.map(
                  (n: ExpiryNotification) => n.index
                );
                const editingNotificationIndex = editing.expiryNotifications.map(
                  (n: ExpiryNotification) => n.index
                );
                return (
                  existingNotificationIndex.length !== editingNotificationIndex.length ||
                  !existingNotificationIndex.every((item: number) =>
                    editingNotificationIndex.includes(item)
                  )
                );
              },
            },
            { name: 'importCode' },
          ],
        }}
      >
        <StyledAddEditAttributeSetting className={`${className ? className : ''}`}>
          <PaperOrDialog
            className={'add-edit-attribute-container-paper'}
            theme={theme}
            dialog={dialogMode}
            paperPadding={{ xs: '40px 24px', md: '56px 64px!important' }}
            title={
              editMode
                ? translate('addEditAttribute.competencyDetails')
                : dialogMode
                  ? translate('addEditAttribute.createNewCompetency')
                  : translate('addEditAttribute.createCompetency')
            }
            titleSx={{ fontSize: { xs: '20px', md: '30px' }, fontWeight: 'bold' }}
            titleSize={1}
            backButton={editMode}
            onBack={this.onCancel.bind(this)}
            actions={actions}
            justifyActions='start'
          >
            <Stack direction='column' spacing={4}>
              {error && (
                // scrollTo because the form is long and this is its only error surface: a rejection
                // from a control further down would otherwise render entirely off-screen.
                <Alert severity='error' scrollTo={true} sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}
              {editMode || !organizationData?.enableStandardsLibrary ? (
                <TextField
                  className='attribute-name-text-field'
                  label={translate('addEditAttribute.competencyNameRequired')}
                  autoComplete={false}
                  autoFocus={true}
                  clearable={false}
                  sx={{ width: '100%' }}
                  value={attribute && attribute.name ? attribute.name : ''}
                  onChange={this.onNameChange.bind(this)}
                  validationMethod={() => this.isNameValid(attribute)}
                  validationText={translate('addEditAttribute.validNameRequired')}
                />
              ) : (
                <Autocomplete
                  freeSolo
                  id='task-name-input'
                  value={attribute && attribute.name ? attribute.name : ''}
                  options={suggestionsCompetencies}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                      return option;
                    }
                    return option?.name || '';
                  }}
                  disableClearable
                  loading={isLoadingSuggestion}
                  noOptionsText={translate('common.noOptions')}
                  onChange={this.onChangeOnSuggestion.bind(this)}
                  renderOption={(props, option, state) => {
                    const matches = match(option.name, state.inputValue, {
                      insideWords: true,
                      requireMatchAll: false,
                    });
                    const parts = parse(option.name, matches);

                    return (
                      <li {...props}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Tooltip title={option.name}>
                            <div
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                              {parts.map((part, index) => (
                                <span
                                  key={index}
                                  className={
                                    'klayo-selector_itempart' +
                                    (part.highlight === true ? ' klayo-selector_highlight' : '')
                                  }
                                >
                                  {part.text}
                                </span>
                              ))}
                            </div>
                          </Tooltip>
                          <i>{translate('addEditAttribute.intelligence')}</i>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('addEditAttribute.competencyNameRequired')}
                      dense={true}
                      inputRef={params.inputProps.ref}
                      disabled={user === null}
                      autoComplete={false}
                      autoFocus={true}
                      value={attribute ? attribute.name : null}
                      onChange={this.onChangeNameWithDebounce.bind(this)}
                      sx={{ width: '100%' }}
                      validationMethod={() => this.isNameValid(attribute)}
                      validationText={translate('addEditAttribute.validNameRequired')}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          type: 'search',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <></>,
                      }}
                    />
                  )}
                />
              )}
              <TextField
                className='description-text-field'
                label={translate('addEditAttribute.description')}
                autoComplete={false}
                multiline={true}
                clearable={false}
                minRows={3}
                sx={{ width: '100%' }}
                value={attribute ? attribute.description : null}
                onChange={this.onDescriptionChange.bind(this)}
              />
              <MultiCategorySelector<SettingsAttributeCategory>
                className='category-multi-selector'
                label={translate('dialogs.competencyCategories')}
                placeHolder={translate('common.search')}
                items={categories ? categories.categories : null}
                values={attribute ? attribute.categories : []}
                onNewItemClick={onShowNewCategoryDialog}
                getItemId={(cat) => cat.categoryId || ''}
                onChange={this.onCategoriesChange.bind(this)}
                onSearch={this.onSearchCategories.bind(this)}
                onBlur={this.onCategoriesBlur.bind(this)}
                isLoading={this.state.isCategoryLoading}
                translate={translate}
              />

              {isParent && (
                <Fade in={true}>
                  <div>
                    <MultiAttributeSelector<SettingsAttribute>
                      label={translate('addEditAttribute.childCompetenciesRequired')}
                      helperText={translate('addEditAttribute.onlyStandardCompetencies')}
                      items={
                        attributes && attribute
                          ? attributes.attributes.filter(
                              (a: SettingsAttribute) =>
                                a.attributeDefinitionId !== attribute.attributeDefinitionId
                            )
                          : null
                      }
                      values={attribute ? attribute.childAttributes : []}
                      onNewItemClick={onShowNewAttributeDialog}
                      itemFilter={(attr) => !attr.isParent}
                      getItemId={(attr) => attr.attributeId || ''}
                      onChange={this.onChildAttributesChange.bind(this)}
                      onSearch={this.onSearchChildAttributes.bind(this)}
                      onBlur={this.onChildAttributeBlur.bind(this)}
                      validationMethod={() => this.isChildAttributesValid(attribute)}
                      validationText={translate('addEditAttribute.selectChildCompetencies')}
                    />
                  </div>
                </Fade>
              )}

              <Stack className='add-edit-attribute-sub-container' gap='24px'>
                {!isParent && orgSettings && orgSettings.showProficiency && (
                  <Fade in={true}>
                    <div>
                      <StyledAttributePaper className='add-edit-attribute-paper'>
                        <Stack
                          className='add-edit-attribute-paper-stack'
                          sx={{ width: '100%' }}
                          justifyContent='space-between'
                          direction='row'
                        >
                          <Switch
                            label={translate('addEditAttribute.proficiencyRequired')}
                            checked={proficiencyEnabled}
                            onChange={this.onProficiencyEnabled.bind(this)}
                            disabled={!orgSettings || !orgSettings.showProficiency}
                          />
                          <TooltipIcon
                            text={
                              orgSettings && !orgSettings.showProficiency
                                ? translate('addEditAttribute.turnOnProficiencyInCompany')
                                : translate('addEditAttribute.proficiencyTooltip')
                            }
                            sx={{ margin: '-8px -8px 0 0' }}
                          />
                        </Stack>
                      </StyledAttributePaper>
                    </div>
                  </Fade>
                )}

                {!isParent && !!organizationData?.enabledFeatures?.Training && (
                  <Fade in={true}>
                    <div>
                      <StyledAttributePaper className='add-edit-attribute-paper'>
                        <Stack
                          className='add-edit-attribute-paper-stack'
                          sx={{ width: '100%' }}
                          justifyContent='space-between'
                          direction='row'
                        >
                          <Switch
                            label={translate('addEditAttribute.training')}
                            checked={trainingEnable}
                            onChange={this.onTrainingEnable.bind(this)}
                            disabled={!organizationData}
                          />
                          <TooltipIcon
                            text={translate('addEditAttribute.trainingTooltip')}
                            sx={{ margin: '-8px -8px 0 0' }}
                          />
                        </Stack>
                        {trainingEnable && (
                          <StyledCourseSelectorSection className='Klayo__course-selector-section'>
                            <MultiCourseSelector<SettingsCourse>
                              label=''
                              helperText=''
                              organization={organizationData ? organizationData : null}
                              items={courses ?? undefined}
                              values={
                                trainingCourseSelected
                                  ? this.addIsValidToCourses.bind(this)(
                                      trainingCourseSelected,
                                      attribute
                                    )
                                  : null
                              }
                              getItemId={(course) => course.id || ''}
                              getItemLabel={(course) => course.courseName ?? ''}
                              onSearch={this.onSearchCourse.bind(this)}
                              onBlur={this.onLoadCourseBlur.bind(this)}
                              onChange={this.onCourseAttributeChange.bind(this)}
                              validationMethod={() =>
                                this.isTrainingCourseValid(trainingCourseSelected)
                              }
                              isValidateOnMount={true}
                              validationText={translate('addEditAttribute.selectTrainingCourses')}
                            />
                            {expiryEnabledAutoOn && (
                              <Alert severity='info' sx={{ marginTop: '6px' }}>
                                {translate('addEditAttribute.recurrentTrainingInfo')}
                              </Alert>
                            )}
                            {isShowWarningTrainingNotMatch && (
                              <Alert
                                icon={<WarningIcon className='klayo-attrtable_warningicon' />}
                                severity='warning'
                                sx={{ marginTop: '6px' }}
                              >
                                {translate('addEditAttribute.expiryDateWarning')}
                                {/* Catalogue courses can be fixed here; integration ones still belong
                                    to the integration settings screen, so only OLC rows get a control. */}
                                {(() => {
                                  const fixable = trainingCourseSelected.filter(
                                    (course: SettingsCourse) =>
                                      course.isCatalogue &&
                                      course.recurrence === COURSE_RECURRENCE.NONE.value
                                  );
                                  if (fixable.length === 0) return null;

                                  // NONE is excluded: it is the state being escaped, never the fix, and
                                  // leaving it selectable gives an option that does nothing at all.
                                  const recurrenceItems = Object.entries(COURSE_RECURRENCE)
                                    .filter(
                                      ([, item]) => item.value !== COURSE_RECURRENCE.NONE.value
                                    )
                                    .map(([key, item]) => [
                                      key,
                                      { ...item, label: translate(item.label) },
                                    ]) as [
                                    string,
                                    (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE],
                                  ][];

                                  return (
                                    <>
                                      <Typography variant='body2' sx={{ marginTop: '8px' }}>
                                        {translate('addEditAttribute.setRecurrenceAppliesToAll')}
                                      </Typography>
                                      {fixable.map((course: SettingsCourse) => (
                                        <Stack
                                          key={course.id}
                                          direction='row'
                                          spacing={1}
                                          alignItems='center'
                                          sx={{ marginTop: '8px' }}
                                        >
                                          <Typography variant='body2' sx={{ flexGrow: 1 }}>
                                            {course.courseName}
                                          </Typography>
                                          <SelectMenu<
                                            (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]
                                          >
                                            label={translate('addEditAttribute.recurrence')}
                                            placeholder={translate('common.select')}
                                            clearable={false}
                                            dense={true}
                                            disabled={isSettingRecurrence}
                                            value={null}
                                            items={recurrenceItems}
                                            onChange={(e) =>
                                              this.onSetCatalogueRecurrence(
                                                course.id ?? '',
                                                (
                                                  e.target
                                                    .value as (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]
                                                ).value
                                              )
                                            }
                                            sx={{ minWidth: '180px' }}
                                          />
                                        </Stack>
                                      ))}
                                    </>
                                  );
                                })()}
                              </Alert>
                            )}
                          </StyledCourseSelectorSection>
                        )}
                      </StyledAttributePaper>
                    </div>
                  </Fade>
                )}

                {!isParent && (
                  <Fade in={true}>
                    <div>
                      <StyledAttributePaper overflow='unset' className='add-edit-attribute-paper'>
                        <Stack
                          className='add-edit-attribute-paper-stack'
                          sx={{ width: '100%' }}
                          justifyContent='space-between'
                          direction='row'
                        >
                          <Switch
                            label={translate('addEditAttribute.expiryDateRequired')}
                            checked={expiryEnabled}
                            onChange={this.onExpiryEnabled.bind(this)}
                          />
                          <TooltipIcon
                            text={translate('addEditAttribute.expiryDateTooltip')}
                            sx={{ margin: '-8px -8px 0 0' }}
                          />
                        </Stack>
                        {expiryEnabled && (
                          <>
                            <ExpiryNotificationSelector
                              theme={theme}
                              onChange={this.onExpiryNotificationChange.bind(this)}
                              values={attribute ? attribute.expiryNotifications : []}
                              sx={{ margin: '20px 0' }}
                              selectSx={{ width: { xs: '180px', sm: '218px' } }}
                            />

                            <Alert severity='info' sx={{ marginTop: '6px' }}>
                              {translate('addEditAttribute.notificationInfo')}
                            </Alert>
                          </>
                        )}
                      </StyledAttributePaper>
                    </div>
                  </Fade>
                )}
              </Stack>

              <Stack
                sx={{ width: '100%' }}
                spacing={2}
                justifyContent='space-between'
                alignItems='end'
                direction='row'
              >
                <TextField
                  label={translate('addEditAttribute.referenceCode')}
                  autoComplete={false}
                  clearable={false}
                  sx={{ width: '100%' }}
                  value={attribute ? attribute.importCode : null}
                  onChange={this.onImportCodeChange.bind(this)}
                />
                <TooltipIcon
                  text={translate('addEditAttribute.referenceCodeTooltip')}
                  sx={{ paddingBottom: '1px' }}
                />
              </Stack>
            </Stack>
          </PaperOrDialog>
        </StyledAddEditAttributeSetting>
      </EditTemplate>
    );
  }
}

const StyledAttributePaper = styled(Paper)`
  &.add-edit-attribute-paper {
    padding: 24px;

    .add-edit-attribute-paper-stack {
      height: 24px;
    }
  }
`;

const StyledCourseSelectorSection = styled.div`
  &.Klayo__course-selector-section {
    margin-top: 24px;

    .klaro-chiparray {
      margin: 24px 0px 0px 0px;
      display: flex;
      gap: 16px;

      &.has-selected {
        margin: 24px 0px;
      }
    }

    .MuiChip-root {
      margin: 0px !important;
      height: 32px;
      padding: 6px 6px 6px 12px;
      gap: 6px;
    }

    .MuiChip-label {
      padding: 0px;
    }

    .MuiChip-deleteIcon {
      margin: 0px;
    }
  }
`;

const StyledAddEditAttributeSetting = styled.div`
  &.klayo__add-edit-attribute-setting {
    .attribute-type-container {
      margin-bottom: 44px;
    }

    .description-text-field {
      margin: 44px 0px 0px 0px;
    }

    .category-multi-selector {
      margin-top: 44px;
    }

    .add-edit-attribute-sub-container {
      margin-top: 40px;
    }

    .attribute-name-text-field {
      margin: 0px;
    }

    @media (max-width: 900px) {
      .MuiPaper-root.add-edit-attribute-container-paper {
        border-radius: 0px;
        border: none;

        h1 {
          color: ${KLAYO_COLORS.GreyGR900};
          font-family: Inter;
          font-size: 20px;
          font-style: normal;
          font-weight: 700;
          line-height: 28px;
          letter-spacing: -0.3px;
          margin-bottom: 32px;
        }

        .description-text-field {
          margin: 38px 0px 0px 0px;
        }

        .category-multi-selector {
          margin-top: 38px;
        }

        .add-edit-attribute-sub-container {
          margin-top: 32px;
        }
      }
    }
  }
`;

export const AddEditAttributeView = withLocale(AddEditAttributeViewBase);
